import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { X, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const rupee = (n) => `₹${(n || 0).toFixed(2)}`;

export default function GodownApprovalsModal({ isOpen, onClose }) {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOrders();
    }
  }, [isOpen]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "Godown Pending Approval" },
      });
      setOrders(res.data.orders || res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch godown approvals");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (orderId, actionStatus) => {
    try {
      await axios.put(
        `${API_URL}/orders/${orderId}`,
        { status: actionStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order ${actionStatus === "rejected" ? "Rejected" : "Approved"}!`);
      setOrders(orders.filter(o => o.id !== orderId));
      if (orders.length === 1) onClose(); // Close if last one
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          className="w-full max-w-lg bg-stone-50 h-full flex flex-col shadow-2xl"
        >
          <div className="p-4 bg-white border-b flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-stone-800">Godown Approvals</h2>
              <p className="text-sm text-stone-500">Review pending Godown orders</p>
            </div>
            <button onClick={onClose} className="p-2 bg-stone-100 hover:bg-stone-200 rounded-full">
              <X size={20} className="text-stone-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-10 text-stone-500">
                No orders pending approval.
              </div>
            ) : (
              orders.map((order) => {
                const targetStatus = order.narration || "completed";
                return (
                  <div key={order.id} className="bg-white p-4 rounded-xl border shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-stone-800">{order.customer_name}</h3>
                        {order.customer_phone && <p className="text-xs text-stone-500">{order.customer_phone}</p>}
                        <p className="text-xs text-amber-600 font-medium mt-1">Requested Action: {targetStatus.replace(/_/g, " ").toUpperCase()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-black text-emerald-600">{rupee(order.total)}</p>
                        <p className="text-xs text-stone-500">ID: {order.invoice_number || order.id.slice(0, 8)}</p>
                      </div>
                    </div>
                    
                    <div className="bg-stone-50 p-2 rounded-lg mb-3">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs py-1 border-b last:border-0 border-stone-200">
                          <span className="text-stone-700">{item.quantity}x {item.product_name}</span>
                          <span className="font-medium">{rupee(item.total)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleAction(order.id, "rejected")}
                        className="flex-1 flex justify-center items-center gap-2 py-2 border border-rose-200 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg text-sm font-semibold transition-colors"
                      >
                        <XCircle size={16} /> Reject
                      </button>
                      <button
                        onClick={() => handleAction(order.id, targetStatus)}
                        className="flex-1 flex justify-center items-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        <CheckCircle size={16} /> Approve
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
